<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto overflow-x-hidden"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-7xl lg:mx-auto"
        >
            <div class="px-4 sm:px-6 lg:px-8">
                <div>
                    <router-link
                        :to="{
                            name: 'ProjectReportsScreen',
                            params: {
                                projectKey: current.projectKey,
                                environmentKey: current.environmentKey,
                            },
                        }"
                        class="inline-flex items-center text-sm font-medium text-taupe-500 hover:text-taupe-800"
                    >
                        Back to Reports
                    </router-link>
                </div>

                <div class="sm:flex sm:items-center mt-2">
                    <div class="sm:flex sm:space-x-4">
                        <h1
                            id="primary-heading"
                            class="text-2xl font-medium text-taupe-800"
                        >
                            Reports
                        </h1>
                    </div>
                </div>

                <div class="mt-2 sm:mt-4 space-y-4">
                    <div class="sm:flex sm:items-center">
                        <div class="sm:flex">
                            <h2 class="text-xl font-medium text-taupe-800">
                                Flag Status Overview
                            </h2>
                        </div>
                    </div>
                    <project-change-notice
                        :project-key="current.projectKey"
                        @refresh="load"
                    />

                    <div
                        class="grid grid-cols-2 gap-3 sm:flex sm:items-end sm:gap-4 rounded-md border border-taupe-200 bg-taupe-50 p-3"
                    >
                        <div>
                            <label
                                for="flag-status-filter-environment"
                                class="block text-sm font-medium text-taupe-700 mb-1"
                            >
                                Environment
                            </label>
                            <select
                                id="flag-status-filter-environment"
                                v-model="filters.environmentKey"
                                data-testid="flag-status-filter-environment"
                                class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                            >
                                <option
                                    v-for="environment in environments"
                                    :key="environment.key"
                                    :value="environment.key"
                                >
                                    {{ environment.name }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <label
                                for="flag-status-filter-status"
                                class="block text-sm font-medium text-taupe-700 mb-1"
                            >
                                Status
                            </label>
                            <select
                                id="flag-status-filter-status"
                                v-model="filters.status"
                                data-testid="flag-status-filter-status"
                                class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                            >
                                <option value="">All statuses</option>
                                <option
                                    v-for="status in statusOptions"
                                    :key="status.value"
                                    :value="status.value"
                                >
                                    {{ status.label }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <label
                                for="flag-status-filter-min-age"
                                class="block text-sm font-medium text-taupe-700 mb-1"
                            >
                                Min age (days)
                            </label>
                            <input
                                id="flag-status-filter-min-age"
                                type="number"
                                min="0"
                                v-model="filters.minAgeDays"
                                data-testid="flag-status-filter-min-age"
                                class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                            />
                        </div>
                        <div>
                            <label
                                for="flag-status-filter-max-age"
                                class="block text-sm font-medium text-taupe-700 mb-1"
                            >
                                Max age (days)
                            </label>
                            <input
                                id="flag-status-filter-max-age"
                                type="number"
                                min="0"
                                v-model="filters.maxAgeDays"
                                data-testid="flag-status-filter-max-age"
                                class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                            />
                        </div>
                        <div>
                            <zen-button
                                variant="secondary"
                                size="field"
                                data-testid="flag-status-apply-filters"
                                @click="applyFilters"
                            >
                                Apply
                            </zen-button>
                        </div>
                    </div>

                    <zen-loading-indicator v-if="loading" />

                    <zen-message-block v-if="error" type="error">
                        {{
                            error.message ||
                            'Failed to load flag status overview. Please try again.'
                        }}
                    </zen-message-block>

                    <template v-if="!loading && !error">
                        <dl
                            class="grid grid-cols-2 gap-px overflow-hidden rounded-xl bg-taupe-50 ring-1 ring-taupe-200 sm:grid-cols-4"
                            data-testid="flag-status-summary"
                        >
                            <div
                                v-for="status in statusOptions"
                                :key="status.value"
                                class="bg-white px-4 py-4 sm:px-5 sm:py-4"
                            >
                                <dt
                                    class="text-xs font-medium uppercase tracking-wider text-taupe-400"
                                >
                                    {{ status.label }}
                                </dt>
                                <dd
                                    class="mt-1.5 text-xl font-medium text-taupe-800"
                                    :data-testid="
                                        'flag-status-summary-' + status.value
                                    "
                                >
                                    {{ summary[status.value] ?? 0 }}
                                </dd>
                            </div>
                        </dl>

                        <div
                            class="-mx-4 mt-8 overflow-hidden shadow-sm sm:-mx-6 md:mx-0 md:rounded-lg"
                        >
                            <table class="min-w-full divide-y divide-taupe-300">
                                <thead class="bg-taupe-50">
                                    <tr>
                                        <th
                                            scope="col"
                                            class="w-full lg:w-2/5 py-3.5 pl-4 pr-3 text-left text-sm lg:text-base font-medium text-taupe-800 sm:pl-6"
                                        >
                                            <button
                                                type="button"
                                                class="flex items-center space-x-1 hover:text-sky-500"
                                                @click="toggleSort('name')"
                                            >
                                                <span>Flag</span>
                                                <span
                                                    v-if="
                                                        filters.sortBy ===
                                                        'name'
                                                    "
                                                    >{{ sortIndicator }}</span
                                                >
                                            </button>
                                        </th>
                                        <th
                                            scope="col"
                                            class="hidden px-3 py-3.5 text-left text-sm lg:text-base font-medium text-taupe-800 lg:table-cell"
                                        >
                                            <button
                                                type="button"
                                                class="flex items-center space-x-1 hover:text-sky-500"
                                                @click="toggleSort('status')"
                                            >
                                                <span>Status</span>
                                                <span
                                                    v-if="
                                                        filters.sortBy ===
                                                        'status'
                                                    "
                                                    >{{ sortIndicator }}</span
                                                >
                                            </button>
                                        </th>
                                        <th
                                            scope="col"
                                            class="hidden px-3 py-3.5 text-left text-sm lg:text-base font-medium text-taupe-800 lg:table-cell"
                                        >
                                            <button
                                                type="button"
                                                class="flex items-center space-x-1 hover:text-sky-500"
                                                @click="
                                                    toggleSort('updated_at')
                                                "
                                            >
                                                <span>Last Modified</span>
                                                <span
                                                    v-if="
                                                        filters.sortBy ===
                                                        'updated_at'
                                                    "
                                                    >{{ sortIndicator }}</span
                                                >
                                            </button>
                                        </th>
                                        <th
                                            scope="col"
                                            class="hidden px-3 py-3.5 text-left text-sm lg:text-base font-medium text-taupe-800 lg:table-cell"
                                        >
                                            <button
                                                type="button"
                                                class="flex items-center space-x-1 hover:text-sky-500"
                                                @click="toggleSort('age')"
                                            >
                                                <span>Age</span>
                                                <span
                                                    v-if="
                                                        filters.sortBy === 'age'
                                                    "
                                                    >{{ sortIndicator }}</span
                                                >
                                            </button>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody
                                    class="divide-y divide-taupe-200 bg-white"
                                >
                                    <tr
                                        v-for="flag in flags"
                                        :key="flag.flag_ulid"
                                        :class="
                                            flag.recently_changed
                                                ? 'bg-sky-50'
                                                : ''
                                        "
                                        :data-testid="
                                            'flag-status-row-' + flag.flag_ulid
                                        "
                                    >
                                        <td
                                            class="w-full max-w-0 py-4 pl-4 pr-3 text-sm lg:text-base font-medium text-taupe-800 sm:w-auto sm:max-w-none sm:pl-6"
                                        >
                                            <div>
                                                {{ flag.flag_name }}
                                                <span
                                                    v-if="flag.recently_changed"
                                                    class="ml-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-sky-100 text-sky-800"
                                                    data-testid="flag-status-recent-badge"
                                                >
                                                    Recently changed
                                                </span>
                                            </div>
                                            <div
                                                class="font-light font-mono text-sm text-taupe-800"
                                            >
                                                <router-link
                                                    v-if="
                                                        flag.status !==
                                                            'archived' &&
                                                        flag.flag_key
                                                    "
                                                    :to="{
                                                        name: 'ProjectFlagDetailScreen',
                                                        params: {
                                                            projectKey:
                                                                current.projectKey,
                                                            environmentKey:
                                                                environmentKeyForLinks,
                                                            key: flag.flag_key,
                                                        },
                                                    }"
                                                    class="flex space-x-1 text-taupe-500 hover:text-sky-500"
                                                >
                                                    <span>{{
                                                        flag.flag_key
                                                    }}</span>
                                                    <zen-icon-open-link
                                                        css-classes="h-4 w-4"
                                                        stroke-width="2"
                                                    />
                                                </router-link>
                                                <span v-else>{{
                                                    flag.flag_key || '—'
                                                }}</span>
                                            </div>
                                            <dl class="font-light lg:hidden">
                                                <dt class="sr-only">Status</dt>
                                                <dd class="mt-1">
                                                    <span
                                                        class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                                                        :class="
                                                            statusBadgeClass(
                                                                flag.status
                                                            )
                                                        "
                                                    >
                                                        {{
                                                            statusLabel(
                                                                flag.status
                                                            )
                                                        }}
                                                    </span>
                                                </dd>
                                                <dt class="sr-only">
                                                    Last Modified
                                                </dt>
                                                <dd
                                                    class="mt-1 truncate text-taupe-800"
                                                >
                                                    <zen-date-time
                                                        human
                                                        :value="flag.updated_at"
                                                    />
                                                </dd>
                                            </dl>
                                        </td>
                                        <td
                                            class="hidden px-3 py-4 text-sm lg:text-base text-taupe-800 lg:table-cell"
                                        >
                                            <span
                                                class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                                                :class="
                                                    statusBadgeClass(
                                                        flag.status
                                                    )
                                                "
                                            >
                                                {{ statusLabel(flag.status) }}
                                            </span>
                                            <div
                                                v-if="
                                                    flag.status ===
                                                    'partial_rollout'
                                                "
                                                class="mt-1 text-xs text-taupe-500"
                                            >
                                                {{ flag.rollout_percentage }}%
                                                rolled out
                                            </div>
                                        </td>
                                        <td
                                            class="hidden px-3 py-4 text-sm lg:text-base text-taupe-800 lg:table-cell"
                                        >
                                            <zen-date-time
                                                human
                                                :value="flag.updated_at"
                                            />
                                            <div class="text-xs lg:text-sm">
                                                <zen-date-time
                                                    :value="flag.updated_at"
                                                />
                                            </div>
                                        </td>
                                        <td
                                            class="hidden px-3 py-4 text-sm lg:text-base text-taupe-800 lg:table-cell"
                                        >
                                            {{ flag.age_days }}
                                            {{
                                                flag.age_days === 1
                                                    ? 'day'
                                                    : 'days'
                                            }}
                                        </td>
                                    </tr>
                                    <tr v-if="flags.length === 0">
                                        <td colspan="4">
                                            <zen-empty-table-message
                                                compact
                                                data-testid="flag-status-empty"
                                                icon="ZenIconChartBar"
                                            >
                                                No flags match the current
                                                filters.
                                            </zen-empty-table-message>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </template>

                    <zen-pagination
                        v-if="!loading && !error"
                        :page="pagination.page"
                        :total-pages="pagination.total_pages"
                        :total="pagination.total"
                        @change="changePage"
                    />

                    <div v-if="!loading" class="text-sm pb-10">
                        Report Generated:
                        <zen-date-time :value="generatedAt" />
                    </div>
                </div>
            </div>
        </section>
    </main>
</template>

<script>
import ReportService from '@/services/report.service'
import { useCurrentEnvironment } from '@/composables/useCurrentEnvironment'
import { useSessionStore } from '@/stores/session'
import { useCancelableLoad } from '@/composables/useCancelableLoad'
import { useServerPagination } from '@/composables/useServerPagination'
import ProjectChangeNotice from '@/screens/_components/project/ProjectChangeNotice.vue'

const STATUS_OPTIONS = [
    { value: 'permanent', label: 'Permanent' },
    { value: 'temporary', label: 'Temporary' },
    { value: 'partial_rollout', label: 'Partial Rollout' },
    { value: 'archived', label: 'Archived' },
]

const STATUS_BADGE_CLASSES = new Map([
    ['permanent', 'bg-green-100 text-green-800'],
    ['temporary', 'bg-taupe-100 text-taupe-800'],
    ['partial_rollout', 'bg-yellow-100 text-yellow-800'],
    ['archived', 'bg-red-100 text-red-800'],
])

export default {
    components: { ProjectChangeNotice },
    name: 'FlagStatusOverview',
    setup() {
        const { current } = useCurrentEnvironment()
        const { loading, error, run } = useCancelableLoad()
        const { pagination, setPage, resetPage, applyResponse, resetOnError } =
            useServerPagination({ perPage: 20 })
        return {
            current,
            loading,
            error,
            run,
            pagination,
            setPage,
            resetPage,
            applyResponse,
            resetOnError,
        }
    },
    data() {
        return {
            summary: {},
            flags: [],
            generatedAt: null,
            environmentKeyForLinks: this.current.environmentKey,
            filters: {
                environmentKey: this.current.environmentKey,
                status: '',
                minAgeDays: '',
                maxAgeDays: '',
                sortBy: 'updated_at',
                sortDir: 'desc',
            },
        }
    },
    computed: {
        environmentScopeKey() {
            return `${this.current.projectKey}:${this.current.environmentKey}`
        },
        sessionStore() {
            return useSessionStore()
        },
        statusOptions() {
            return STATUS_OPTIONS
        },
        environments() {
            const project = this.sessionStore.data?.projects?.data?.find(
                (p) => p.key === this.current.projectKey
            )

            return project?.environments?.data ?? []
        },
        sortIndicator() {
            return this.filters.sortDir === 'asc' ? '▲' : '▼'
        },
    },
    watch: {
        environmentScopeKey() {
            this.applyFilters()
        },
    },
    methods: {
        applyFilters() {
            this.resetPage()
            this.load()
        },
        changePage(page) {
            this.setPage(page)
            this.load()
        },
        statusLabel(status) {
            return (
                STATUS_OPTIONS.find((option) => option.value === status)
                    ?.label ?? status
            )
        },
        statusBadgeClass(status) {
            return (
                STATUS_BADGE_CLASSES.get(status) ??
                'bg-taupe-100 text-taupe-800'
            )
        },
        toggleSort(sortBy) {
            if (this.filters.sortBy === sortBy) {
                this.filters.sortDir =
                    this.filters.sortDir === 'asc' ? 'desc' : 'asc'
            } else {
                this.filters.sortBy = sortBy
                this.filters.sortDir = 'desc'
            }

            this.applyFilters()
        },
        load() {
            this.run(
                (signal) =>
                    ReportService.getFlagStatusOverview(
                        this.current.projectKey,
                        this.current.environmentKey,
                        {
                            environmentKey:
                                this.filters.environmentKey || undefined,
                            status: this.filters.status || undefined,
                            minAgeDays: this.filters.minAgeDays || undefined,
                            maxAgeDays: this.filters.maxAgeDays || undefined,
                            sortBy: this.filters.sortBy,
                            sortDir: this.filters.sortDir,
                            page: this.pagination.page,
                            perPage: this.pagination.per_page,
                        },
                        { signal }
                    ),
                (response) => {
                    this.summary = response.data.summary
                    this.flags = response.data.flags
                    this.environmentKeyForLinks = response.data.environment.key
                    this.generatedAt = response.data.generated_at
                    if (this.applyResponse(response.data.pagination)) {
                        this.load()
                    }
                },
                () => {
                    this.summary = {}
                    this.flags = []
                    this.resetOnError()
                }
            )
        },
    },
    mounted() {
        this.load()
    },
}
</script>
